import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/**
 * LIS (Epic Beaker) test orders.
 *
 * Everything here goes to OUR backend, which then calls the Integration Service.
 * The frontend never talks to the relay and never holds the LIS order token —
 * that is a service secret, and the backend is the only place it lives. Tenant
 * scope rides along on `X-Tenant-Id`, injected by `authHeaders`.
 */

/** What Beaker said. Each value means something different for the lab tech. */
export type LisOrderStatus =
  /** Waiting for an answer; only seen if a read races the place call. */
  | 'pending'
  /** Beaker acknowledged (AA). Wait for results. */
  | 'accepted'
  /** Beaker refused it (AE/AR). Correct it and place a NEW order — never resend this one. */
  | 'rejected'
  /** Nothing acknowledged. May be retried under the SAME order number. */
  | 'undelivered'

export type LisTestSummary = { code: string; name: string }

export type LisCatalog = {
  /** False when this deployment has no LIS endpoint configured at all. */
  configured: boolean
  /** True only on dev: the mock's simulated Beaker can be driven by parameters. */
  simulationAllowed: boolean
  tests: LisTestSummary[]
}

export type LisOrder = {
  id: string
  orderId: string
  testCode: string
  testName: string | null
  mrn: string
  productId: string | null
  din: string | null
  productDescriptionCode: string | null
  patientId: string | null
  procedureId: string | null
  formDefinitionId: string | null
  stage: string | null
  status: LisOrderStatus
  /** MSA-1: AA / AE / AR. */
  ackCode: string | null
  /** MSH-10 of the ORM^O01 that was sent. */
  controlId: string | null
  /** Beaker's MSA-3 on a rejection, or why nothing was delivered. */
  reason: string | null
  sentAt: string | null
  answeredAt: string | null
  placedBy: string | null
  createdAt: string
  updatedAt: string
  /** The relay had already answered this order number and did not send it again. */
  duplicate?: boolean
  /** The ordering provider sent (ORC-12 / OBR-16), as it was at the time. */
  orderingProvider?: { npi: string; lastName?: string; firstName?: string; middleName?: string } | null
  /** fixed | choose, or null when the test had no provider source. */
  orderingProviderSource?: string | null
  /** On placement: the test's source found no provider and it went without one. */
  providerMissing?: boolean
}

/** Where in the procedure the specimen was taken. Kept in Title21; never sent to Beaker. */
export type LisStage = 'Pre' | 'Post'

export const LIS_STAGES: LisStage[] = ['Pre', 'Post']

/** One OBX, plus whether it actually reaches a dose figure. */
export type LisObservation = {
  id: string
  setId: number | null
  valueType: string | null
  code: string
  codeText: string | null
  value: string | null
  valueNumeric: number | null
  valueCode: string | null
  valueCodeText: string | null
  valueCodeSystem: string | null
  units: string | null
  referenceRange: string | null
  abnormalFlag: string | null
  status: string | null
  subId: string | null
  observedAt: string | null
  performer: string | null
}

export type LisResult = {
  id: string
  orderId: string | null
  fillerOrderId: string
  testCode: string
  testName: string | null
  resultStatus: string | null
  serviceSection: string | null
  sentAt: string | null
  notDirectlyOrdered: boolean
  stage: string | null
  parentValue: string | null
  createdAt: string
  observations: LisObservation[]
}

/** A patient's result: its order's product, when the specimen was a product's. */
export type PatientLisResult = LisResult & {
  productId: string | null
  din: string | null
  productDescriptionCode: string | null
}

export type PlaceLisOrderBody = {
  /** The number shown in the dialog. The response carries the one actually used. */
  orderId?: string
  testCode: string
  mrn: string
  /**
   * WHICH BAG is being tested. Required: Beaker is never told what a product
   * is, so the order number is the only thing that can carry a result back to
   * one, and only if we recorded the product here when the order was placed.
   */
  productId: string
  patientId?: string
  procedureId?: string
  formDefinitionId?: string
  stage?: LisStage
  specimenSource?: string
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders(init?.body ? { 'Content-Type': 'application/json' } : undefined),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

export type PlaceLisOrdersBody = {
  productId: string
  mrn: string
  testCodes: string[]
  stage?: string
  patientId?: string
  procedureId?: string
  formDefinitionId?: string
  specimenSource?: string
  /** One per "choose when ordering" test, as entered in the order dialog. */
  orderingProviders?: {
    testCode: string
    npi?: string
    lastName?: string
    firstName?: string
    middleName?: string
  }[]
}

export type PlaceLisOrdersResult = {
  placed: LisOrder[]
  failed: { testCode: string; error: string }[]
  requested: number
}

export const lisApi = {
  catalog: () => request<LisCatalog>('/integrations/lis/catalog'),

  /**
   * An order number to SHOW before the order exists. Minted, not reserved —
   * abandoning the dialog leaves no record behind.
   */
  nextOrderId: () => request<{ orderId: string }>('/integrations/lis/order-id'),

  resultsForProduct: (productId: string) =>
    request<LisResult[]>(`/integrations/lis/results?productId=${encodeURIComponent(productId)}`),

  /** Every result for one patient, each with the product its order named (z8nrz7g57g). */
  resultsForPatient: (mrn: string) =>
    request<PatientLisResult[]>(`/integrations/lis/results?mrn=${encodeURIComponent(mrn)}`),

  listOrders: (params: { patientId?: string; procedureId?: string; productId?: string } = {}) => {
    const query = new URLSearchParams()
    if (params.patientId) query.set('patientId', params.patientId)
    if (params.procedureId) query.set('procedureId', params.procedureId)
    if (params.productId) query.set('productId', params.productId)
    const suffix = query.toString() ? `?${query.toString()}` : ''
    return request<LisOrder[]>(`/integrations/lis/orders${suffix}`)
  },

  /** One order per test code, all at the same stage. */
  placeOrders: (body: PlaceLisOrdersBody) =>
    request<PlaceLisOrdersResult>('/integrations/lis/orders/batch', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  placeOrder: (body: PlaceLisOrderBody) =>
    request<LisOrder>('/integrations/lis/orders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Resend an UNDELIVERED order under the same order number. The backend 409s
   * for any other status: a rejected order must be corrected and placed anew.
   */
  retryOrder: (orderId: string) =>
    request<LisOrder>(`/integrations/lis/orders/${encodeURIComponent(orderId)}/retry`, {
      method: 'POST',
    }),

  /**
   * Apply Test Results (z8nrz7g2wm): hand the laboratory values typed on a form
   * to one product, signed. The server refuses (400, naming them) while a value
   * of the stage that must be filled is empty.
   */
  applyTestResults: (body: ApplyTestResultsBody) =>
    request<ApplyTestResultsResult>('/integrations/lis/manual-results', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
}

export type ApplyTestResultsBody = {
  submissionId: string
  productId: string
  /** The form's LIS stage the values are for; omitted on a form with no stages. */
  stage?: string
  signingToken: string
}

export type ApplyTestResultsResult = {
  productId: string
  din: string
  productCode: string
  stage: string | null
  /** How many values were applied. */
  applied: number
  /** Whether the product's dose figures were derived from them, and if not why. */
  lims: { applied: boolean; reason?: string }
}
